import { defineStore } from 'pinia';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import dot from 'dot-object';
import {
  INPUT_MAPPING_SOURCE,
  INPUT_MAPPING_TARGET,
  type InputFieldPath,
  type InputMappingBackendResponse,
  InputMappingFieldDefinition,
  InputMappingInformation,
  type InputMappingPathValue,
  InputMappingPriority,
  InputMappingSourceLabel,
  type InputMappingStoreState,
  type InputMappingValue,
} from '@/features/InputMapping/types';
import { useApi } from '@/services/api/useApi';
import { getEntries } from '@/utils/objects/objects';
import { useContractStore, useInputStore } from '@/features/Sparten';
import set from 'lodash/set';
import type { ChoiceValueValidation } from '@/features/ChoicesLists/types';
import { useForm } from '@/utils/form/useForm';
import { validateChoiceValues } from '@/features/ChoicesLists/utils';
import { getCurrentSpartenConfig } from '@/utils/getCurrentSpartenConfig/getCurrentSpartenConfig';
import type { UseLogger } from '@/services/logger/useLogger';
import { transformDateStrings } from '@/utils/dates/transformDates';
import { useCompaniesStore } from '@/features/Companies/store';
import { ROLE, useClient, useClientStore } from '@/features/Clients';
import get from 'lodash/get';
import { useQuoteStore } from '@/features/Quotes/store';
import { QUOTE_TYPE } from '@/features/Quotes/types';

export const useInputMappingStore = defineStore('InputMappingStore', {
  state: (): InputMappingStoreState => ({
    data: {},
    invalidChoices: [],
    isLoading: false,
    inventoryContract: undefined,
  }),

  actions: {
    clearData() {
      this.data = {};
      this.invalidChoices = [];
      this.inventoryContract = undefined;
    },

    async loadVersicherungsnehmer(
      id: string,
    ): Promise<InputMappingBackendResponse> {
      await useClientStore().loadClientById(id, undefined, true);

      return new Promise((resolve) => {
        const vnProxy = useClient(ROLE.VERSICHERUNGSNEHMER);

        const mappedData: Record<InputFieldPath, InputMappingInformation> = {};

        const { versicherungsnehmer } = getCurrentSpartenConfig().clientFields;

        if (versicherungsnehmer === undefined) {
          throw new VglError(VGL_ERROR_CODE.INPUT_MAPPING_MISSING_VN);
        }

        Object.values(versicherungsnehmer).forEach((path) => {
          const value = get(
            vnProxy.client.value.data,
            path,
          ) as InputMappingPathValue;

          if (value !== null) {
            mappedData[path] = {
              value,
              hint: null,
              valueReplacementText: null,
            };
          }
        });

        const data: InputMappingBackendResponse = {
          sources: {
            [INPUT_MAPPING_SOURCE.VERSICHERUNGSNEHMER]: {
              name: INPUT_MAPPING_SOURCE.VERSICHERUNGSNEHMER,
              fields: {
                clientData: mappedData,
              },
            },
          },
        };

        resolve(data);
      });
    },

    async loadInventoryContract(
      inventoryContractId: number,
    ): Promise<InputMappingBackendResponse> {
      const inventoryContractData = await useQuoteStore().loadQuoteById(
        inventoryContractId.toString(),
        QUOTE_TYPE.INVENTORY_CONTRACT,
        true,
      );

      const inputData = dot.dot(
        inventoryContractData?.content.searchData.eingabedaten ?? {},
      );
      const contractData = dot.dot(
        inventoryContractData?.content.contractData ?? {},
      );

      const mapToStructure = (input: Record<string, unknown>) => {
        return Object.keys(input).reduce((acc, key) => {
          acc[key] = {
            value: input[key] as InputMappingPathValue,
            hint: null,
            valueReplacementText: null,
          };

          return acc;
        }, {} as InputMappingFieldDefinition);
      };

      return new Promise((resolve) => {
        const data: InputMappingBackendResponse = {
          sources: {
            [INPUT_MAPPING_SOURCE.INVENTORY_CONTRACT]: {
              name: INPUT_MAPPING_SOURCE.INVENTORY_CONTRACT,
              fields: {
                searchData: {
                  eingabedaten: mapToStructure(inputData),
                },
                contractData: mapToStructure(contractData),
              },
            },
          },
        };

        resolve(data);
      });
    },

    async loadSources(
      sources: Partial<Record<INPUT_MAPPING_SOURCE, string>>,
      logger: UseLogger,
    ) {
      try {
        if (!(await useCompaniesStore().waitUntilLoaded())) {
          throw new VglError(VGL_ERROR_CODE.COMPANIES_LOADING_FAILED, {
            additionalErrorMessage: 'input mapping -> loadSources',
          });
        }

        this.clearData();

        this.isLoading = true;

        const loadPromises: Promise<InputMappingBackendResponse | null>[] = [];

        getEntries(sources).forEach(([source, id]) => {
          if (id === undefined) return;

          if (source === INPUT_MAPPING_SOURCE.VERSICHERUNGSNEHMER) {
            logger.info(`loading customer ${id}`);
            loadPromises.push(this.loadVersicherungsnehmer(id));
          } else {
            const [, request] = useApi().inputMapping.loadSourceByTypeAndId(
              source,
              id,
            );

            loadPromises.push(request);
          }
        });

        const inputStore = useInputStore();
        const contractStore = useContractStore();

        const handleSourceMappingResponse = (
          sourceMapping: (InputMappingBackendResponse | null)[],
        ) => {
          Object.values(sourceMapping).forEach((inputMappingResult) => {
            if (inputMappingResult === null) {
              throw new VglError(
                VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE,
                {
                  additionalErrorMessage:
                    'input mapping -> loadSourceByTypeAndId',
                },
              );
            }

            Object.values(inputMappingResult.sources).forEach((source) => {
              if (source.fields.searchData !== undefined) {
                this.parseData(
                  transformDateStrings(source.fields.searchData.eingabedaten),
                  source.name,
                  INPUT_MAPPING_TARGET.INPUT,
                );
              }

              if (source.fields.contractData !== undefined) {
                this.parseData(
                  transformDateStrings(source.fields.contractData),
                  source.name,
                  INPUT_MAPPING_TARGET.CONTRACT,
                );
              }

              if (source.fields.clientData !== undefined) {
                this.parseData(
                  transformDateStrings(source.fields.clientData),
                  source.name,
                  INPUT_MAPPING_TARGET.VERSICHERUNGSNEHMER,
                );
              }
            });

            const { bestandsvertrag } = inputMappingResult;

            if (bestandsvertrag) {
              this.inventoryContract = bestandsvertrag;
            }
          });
        };

        // 1. gather sources and build data structure
        await Promise.all(loadPromises).then((sourceMapping) =>
          handleSourceMappingResponse(sourceMapping),
        );

        // 1.1. additional check for optional matching inventory contract
        if (this.inventoryContract) {
          const inventoryContractData = await this.loadInventoryContract(
            this.inventoryContract.id,
          );
          handleSourceMappingResponse([inventoryContractData]);
        }

        // nur die Feldpfade: die Werte sind Kundendaten aus GDV/Bestandsvertrag (VGL-7106)
        logger.info(
          'data has been prepared for validation',
          Object.keys(this.data),
        );

        // 2. prepare choice validation
        const choiceValidation: ChoiceValueValidation = {};
        const { choicesFields } = useForm();

        getEntries(this.data).forEach(([path, mappingValue]) => {
          const choice = choicesFields.get(path);

          if (choice !== undefined) {
            choiceValidation[choice] = {
              path,
              value: mappingValue.value,
            };
          }
        });

        const invalidChoices = await validateChoiceValues(choiceValidation);
        this.invalidChoices = Object.values(invalidChoices).map(
          (validationResult) => validationResult.path,
        );

        logger.info('invalid choices result', this.invalidChoices);

        // 3. apply valid data
        const appliedInfo: string[] = [];
        getEntries(this.data).forEach(([path, mappingValue]) => {
          if (
            !this.invalidChoices.includes(path) &&
            mappingValue.value !== '' &&
            mappingValue.value !== null
          ) {
            set(
              mappingValue.target !== INPUT_MAPPING_TARGET.CONTRACT
                ? inputStore
                : contractStore,
              path,
              mappingValue.value,
            );
            appliedInfo.push(
              `${path} from ${mappingValue.source} into ${mappingValue.target}`,
            );
          } else {
            delete this.data[path];
          }
        });

        logger.info('applied', appliedInfo);
      } catch (error) {
        if (
          error instanceof VglError &&
          error.getErrorCode() !== VGL_ERROR_CODE.API_AXIOS_ERROR
        ) {
          throw error;
        }

        throw new VglError(VGL_ERROR_CODE.INPUT_MAPPING_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - ${JSON.stringify(sources)}`,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },

    parseData(
      dataToParse: InputMappingFieldDefinition,
      source: INPUT_MAPPING_SOURCE,
      target: INPUT_MAPPING_TARGET,
    ) {
      getEntries(dataToParse).forEach(([path, information]) => {
        const mappingValue: InputMappingValue = {
          ...information,
          source,
          target,
        };

        if (this.data[path] === undefined || this.data[path].value === null) {
          this.data[path] = mappingValue;
        } else {
          const prioIndexCurrent = InputMappingPriority.indexOf(
            this.data[path].source,
          );
          const prioIndexNew = InputMappingPriority.indexOf(
            mappingValue.source,
          );

          if (prioIndexNew < prioIndexCurrent) {
            this.data[path] = mappingValue;
          }
        }
      });
    },
  },

  getters: {
    mappedPaths(state) {
      return Object.keys(state.data);
    },

    getSourceDisplayInformation(state): (path: string) => string {
      return (path) => {
        const info = get(state.data, path);

        if (info === undefined) {
          throw new VglError(
            VGL_ERROR_CODE.INPUT_MAPPING_UNKNOWN_PATH_SOURCE_LOOKUP,
            {
              additionalData: path,
            },
          );
        }

        return `Quelle: ${InputMappingSourceLabel[info.source]}`;
      };
    },

    getValue(state): (path: string) => any {
      return (path) => {
        const info = get(state.data, path);

        if (info !== undefined) {
          return info.valueReplacementText ?? info.value;
        }

        return '';
      };
    },

    hasInventoryContract(state) {
      return state.inventoryContract != null;
    },
  },
});
